import { useDispatch, useSelector } from 'react-redux';
import { Divider, Button, CircularProgress } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { User } from '@auth0/auth0-react';
import { GVUserAvatar } from 'components';
import { GVTypography } from 'components/lib';
import { Formik, Field, FieldProps, Form } from 'formik';
import { fetchActiveUserOrganization, updateUserData } from 'store/myAccount/requests';
import { useMutation, useQuery } from '@redux-requests/react';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import { getAppMetadata, getCreatedAt } from 'utils/auth';
import { app, getDisplayName } from 'store';
import MyAccountCard from '../Card';
import { useUserStateStore } from 'zstore/userStateStore';
import styles from './General.module.css';

export interface SettingsFormData {
  firstName: string;
  lastName: string;
}

const General = () => {
  const dispatch = useDispatch();
  const displayName = useSelector(getDisplayName);
  const { loading: loadingSaveChanges } = useMutation(updateUserData);
  const { data: organization } = useQuery({ type: fetchActiveUserOrganization });
  const { user } = useUserStateStore();
  const createdAt = getCreatedAt(user as User);
  const { given_name: givenName = '', family_name: familyName = ' ', email } = user || {};
  const isScimManaged = organization?.scimManaged === true && getAppMetadata(user as User).scim_managed === true;

  const date = new Date(createdAt);
  const joined = new Intl.DateTimeFormat('en-GB').format(date).replace(/\//g, '-');

  const initialValues: SettingsFormData = {
    firstName: givenName,
    lastName: familyName,
  };

  return (
    <MyAccountCard
      avatar={<GVUserAvatar className={styles.avatar} />}
      title={
        <div className={styles.titleBlock}>
          <div className={styles.name}>
            <GVTypography className={styles.title} id="name-title">
              {displayName}
            </GVTypography>
          </div>
          <div className={styles.joined}>
            <GVTypography
              emphasis="medium"
              variant="subtitle1"
              icon={<AccessTimeIcon fontSize="small" className={styles.joinedIcon} />}
            >
              {`Date joined: ${joined}`}
            </GVTypography>
          </div>
        </div>
      }
    >
      <>
        <Divider light />
        <div className={styles.info}>
          <GVTypography emphasis="high" variant="subtitle1">
            Team member info
          </GVTypography>
          <Formik
            key="settingsForm"
            initialValues={initialValues}
            validate={(values: SettingsFormData) => {
              const errors: Partial<SettingsFormData> = {};
              if (!values.firstName.trim()) {
                errors.firstName = 'Required field';
              }
              if (!values.lastName.trim()) {
                errors.lastName = 'Required field';
              }
              return errors;
            }}
            onSubmit={(values, actions) => {
              const onUpdateDone = () => {
                actions.resetForm({ values }); // @todo check if it works ???
              };
              dispatch(updateUserData(values, initialValues, actions, onUpdateDone));
              dispatch(app.actions.setDisplayName(values.firstName.concat(' ', values.lastName)));
            }}
          >
            {(props) => (
              <Form onSubmit={props.handleSubmit} className={styles.form}>
                <div className={styles.fields}>
                  <Field name="firstName">
                    {(fieldProps: FieldProps) => (
                      <GVTextField
                        name={fieldProps.field.name}
                        hasError={!!fieldProps.meta.error}
                        message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                        onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                        label="First Name"
                        value={fieldProps.field.value}
                        id="first-name-input"
                        maxLength={25}
                        isDisabled={isScimManaged}
                      />
                    )}
                  </Field>
                  <Field name="lastName">
                    {(fieldProps: FieldProps) => (
                      <GVTextField
                        name={fieldProps.field.name}
                        hasError={!!fieldProps.meta.error}
                        message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                        onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                        label="Last Name"
                        value={fieldProps.field.value}
                        id="last-name-input"
                        maxLength={25}
                        isDisabled={isScimManaged}
                      />
                    )}
                  </Field>
                </div>
                <GVTextField label="Email" value={email} isDisabled id="email-input" />
                <div className={styles.rule}>
                  <Divider light />
                </div>
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={!props.dirty || isScimManaged}
                  id="user-save-button"
                  data-testid="user_save_changes"
                  className={styles.submit}
                >
                  Save changes
                  {loadingSaveChanges && <CircularProgress className={styles.saving} size={16} color="primary" />}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      </>
    </MyAccountCard>
  );
};

export default General;
